import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Check, Pencil, Plus, RotateCcw, Trash2, User } from 'lucide-react';

import { errorMessage } from '@/api/client';
import { agendaApi, customersApi } from '@/api/resources';
import { EmptyState, Loading, PageHeader, StatCard, SubmitButton } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, isPast, toDateTimeLocal } from '@/lib/dates';

const NO_CUSTOMER = 'sin-cliente';

const TYPE_STYLES = {
  tarea: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  cita: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',
  seguimiento: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
};

const emptyForm = () => ({
  title: '',
  event_type: 'tarea',
  description: '',
  scheduled_datetime: toDateTimeLocal(new Date()),
  customer_id: NO_CUSTOMER,
  contact_name: '',
});

export default function Agenda() {
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('pendiente');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      setEvents(await agendaApi.list());
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo cargar la agenda'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    customersApi.list().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  const visible = useMemo(
    () => (filter === 'todos' ? events : events.filter((e) => e.status === filter)),
    [events, filter]
  );

  const counts = useMemo(() => {
    const pending = events.filter((e) => e.status === 'pendiente');
    return {
      pending: pending.length,
      overdue: pending.filter((e) => isPast(e.scheduled_datetime)).length,
      done: events.length - pending.length,
    };
  }, [events]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setForm({
      title: event.title,
      event_type: event.event_type,
      description: event.description || '',
      scheduled_datetime: toDateTimeLocal(event.scheduled_datetime),
      customer_id: event.customer_id || NO_CUSTOMER,
      contact_name: event.contact_name || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error('Poné un título');
      return;
    }
    if (!form.scheduled_datetime) {
      toast.error('Elegí fecha y hora');
      return;
    }

    setSaving(true);
    const payload = {
      title: form.title.trim(),
      event_type: form.event_type,
      description: form.description.trim() || null,
      // El input datetime-local no lleva zona: lo pasamos a ISO con la del navegador
      scheduled_datetime: new Date(form.scheduled_datetime).toISOString(),
      customer_id: form.customer_id === NO_CUSTOMER ? null : form.customer_id,
      contact_name: form.contact_name.trim() || null,
    };

    try {
      if (editing) {
        await agendaApi.update(editing.id, payload);
        toast.success('Evento actualizado');
      } else {
        await agendaApi.create(payload);
        toast.success('Evento agendado');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo guardar el evento'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (event) => {
    try {
      await agendaApi.toggle(event.id);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo cambiar el estado'));
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`¿Eliminar "${event.title}"?`)) return;
    try {
      await agendaApi.remove(event.id);
      toast.success('Evento eliminado');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar el evento'));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Agenda" subtitle="Tareas, citas y seguimientos">
        <Button onClick={openNew} className="rounded-sm">
          <Plus size={18} className="mr-2" />
          Nuevo evento
        </Button>
      </PageHeader>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Pendientes" value={counts.pending} icon={CalendarClock} />
        <StatCard
          label="Vencidos"
          value={counts.overdue}
          icon={RotateCcw}
          tone={counts.overdue ? 'negative' : 'default'}
        />
        <StatCard label="Completados" value={counts.done} icon={Check} tone="positive" />
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { value: 'pendiente', label: 'Pendientes' },
          { value: 'completado', label: 'Completados' },
          { value: 'todos', label: 'Todos' },
        ].map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={filter === option.value ? 'default' : 'outline'}
            className="rounded-sm"
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title={filter === 'pendiente' ? 'Nada pendiente' : 'Sin eventos'}
          description="Agendá una tarea, una cita con un cliente o un seguimiento."
          action={
            <Button onClick={openNew} className="rounded-sm">
              <Plus size={18} className="mr-2" />
              Nuevo evento
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {visible.map((event) => {
            const done = event.status === 'completado';
            const overdue = !done && isPast(event.scheduled_datetime);
            return (
              <div
                key={event.id}
                className={`flex items-start gap-3 rounded-sm border bg-card p-3 ${
                  overdue ? 'border-red-300 dark:border-red-900' : 'border-border'
                } ${done ? 'opacity-60' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => handleToggle(event)}
                  aria-label={done ? 'Marcar pendiente' : 'Marcar completado'}
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                    done
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : 'border-muted-foreground hover:border-primary'
                  }`}
                >
                  {done && <Check size={13} />}
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        TYPE_STYLES[event.event_type]
                      }`}
                    >
                      {event.event_type}
                    </span>
                    <span className={`font-medium ${done ? 'line-through' : ''}`}>
                      {event.title}
                    </span>
                  </div>
                  <p
                    className={`mt-0.5 text-xs ${
                      overdue ? 'font-medium text-red-600 dark:text-red-400' : 'text-muted-foreground'
                    }`}
                  >
                    {formatDateTime(event.scheduled_datetime)}
                    {overdue && ' · vencido'}
                  </p>
                  {(event.customer_name || event.contact_name) && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <User size={11} />
                      {event.customer_name || event.contact_name}
                    </p>
                  )}
                  {event.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{event.description}</p>
                  )}
                </div>

                <div className="flex shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(event)} aria-label="Editar">
                    <Pencil size={15} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(event)}
                    aria-label="Eliminar"
                  >
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar evento' : 'Nuevo evento'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="a-title">Título *</Label>
              <Input
                id="a-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                className="rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={form.event_type}
                  onValueChange={(event_type) => setForm({ ...form, event_type })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tarea">Tarea</SelectItem>
                    <SelectItem value="cita">Cita</SelectItem>
                    <SelectItem value="seguimiento">Seguimiento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="a-when">Fecha y hora *</Label>
                <Input
                  id="a-when"
                  type="datetime-local"
                  value={form.scheduled_datetime}
                  onChange={(e) => setForm({ ...form, scheduled_datetime: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select
                value={form.customer_id}
                onValueChange={(customer_id) => setForm({ ...form, customer_id })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CUSTOMER}>Sin cliente</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.customer_id === NO_CUSTOMER && (
              <div className="space-y-2">
                <Label htmlFor="a-contact">Contacto (si no es un cliente cargado)</Label>
                <Input
                  id="a-contact"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="a-desc">Descripción</Label>
              <Textarea
                id="a-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <SubmitButton loading={saving}>{editing ? 'Guardar' : 'Agendar'}</SubmitButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
